import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { Typography, Paper, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, TextField, Button, Grid, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip, Checkbox, makeStyles } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import axios from 'axios';
import { connect } from 'redux-zero/react';
import actions from '../../actions';
import moment from 'moment';
import { formatMoney, accountLabel } from '../../utils';
import PeriodSelector from '../report/PeriodSelector';
import DateField from '../DateField';
import StatementImportDialog from './StatementImportDialog';
import { typeNames, itemName } from './labels';

const useStyles = makeStyles(theme => ({
    topBar: {
        marginBottom: 10
    },
    topBarControl: {
        marginRight: 10,
        marginTop: 6
    },
    statusSelect: {
        minWidth: 120
    },
    summary: {
        marginBottom: 10,
        padding: theme.spacing(1, 2)
    },
    summaryRow: {
        display: 'flex',
        justifyContent: 'space-between',
        maxWidth: 520,
        padding: '2px 0'
    },
    summaryTotal: {
        display: 'flex',
        justifyContent: 'space-between',
        maxWidth: 520,
        padding: '4px 0',
        borderTop: '1px solid #ccc',
        fontWeight: 'bold'
    },
    difference: {
        color: 'red'
    },
    bankDate: {
        width: 180
    },
    bankDateCell: {
        display: 'flex',
        alignItems: 'flex-start'
    },
    clearedCheckbox: {
        marginTop: 2
    }
}));

// "" when the entry is not cleared
function savedBankDate(row) {
    return row.bankDate ? String(row.bankDate).substring(0, 10) : '';
}

const mapToProps = ({ selectedSociety, currentPeriod }) => ({ selectedSociety, currentPeriod });

function BankReconciliation({ selectedSociety, currentPeriod }) {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [accounts, setAccounts] = useState([]);
    const [accountCode, setAccountCode] = useState('');
    const [fromDate, setFromDate] = useState(currentPeriod.startDate);
    const [toDate, setToDate] = useState(currentPeriod.endDate);
    const [status, setStatus] = useState('uncleared');
    const [reconciliation, setReconciliation] = useState({ items: [], bookBalance: 0 });
    const [bankDates, setBankDates] = useState({});         // Edited bank dates by entry id
    const [statementBalance, setStatementBalance] = useState('');
    const [refreshData, setRefreshData] = useState(1);
    const [showReconcileUpTo, setShowReconcileUpTo] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [reconcileUpToDate, setReconcileUpToDate] = useState('');

    useEffect(() => {
        axios.get('/account/selectitems/bank', { headers: { 'X-SocietyId': selectedSociety } })
            .then(response => {
                setAccounts(response.data);
            });
    }, [selectedSociety]);

    useEffect(() => {
        if (!accountCode || moment(fromDate).year() < 1000 || moment(toDate).year() < 1000) return;

        axios.get('/reconciliation/' + accountCode + '/' + moment(fromDate).format('YYYY-MM-DD') + '/' + moment(toDate).format('YYYY-MM-DD'),
            { headers: { 'X-SocietyId': selectedSociety } })
            .then(response => {
                setReconciliation(response.data);
                setBankDates({});
            })
            .catch(error => {
                enqueueSnackbar((error.response && error.response.data && error.response.data.message) || 'Loading failed', { variant: 'error' });
            });
    }, [selectedSociety, accountCode, fromDate, toDate, refreshData, enqueueSnackbar]);

    function bankDate(row) {
        return bankDates[row.id] !== undefined ? bankDates[row.id] : savedBankDate(row);
    }

    function bankDateError(row) {
        const date = bankDate(row);
        return date && moment(date).isBefore(moment(row.date), 'day') ? 'Before transaction date' : '';
    }

    function isOutstanding(row) {
        const date = bankDate(row);
        return !date || moment(date).isAfter(moment(toDate), 'day');
    }

    // Uncleared and cleared go by the saved bank date so rows don't disappear while being edited
    const rows = reconciliation.items.filter(row =>
        status === 'all' || (status === 'cleared') === Boolean(savedBankDate(row)));

    const changedRows = reconciliation.items.filter(row => bankDates[row.id] !== undefined && bankDates[row.id] !== savedBankDate(row));
    const hasErrors = reconciliation.items.some(row => bankDateError(row));

    const outstanding = reconciliation.items.filter(isOutstanding);
    const notCredited = outstanding.reduce((sum, row) => sum + row.deposit, 0);
    const notPresented = outstanding.reduce((sum, row) => sum + row.withdrawal, 0);
    const bankBalance = reconciliation.bookBalance - notCredited + notPresented;
    const difference = statementBalance === '' ? null : parseFloat(statementBalance) - bankBalance;

    function save() {
        axios({
            method: 'PUT',
            url: '/reconciliation/bankdates',
            headers: { 'X-SocietyId': selectedSociety },
            data: {
                accountCode: accountCode,
                items: changedRows.map(row => ({ id: row.id, bankDate: bankDates[row.id] || null }))
            }
        })
            .then(() => {
                enqueueSnackbar('Saved', { variant: 'success' });
                setRefreshData(refreshData + 1);
            })
            .catch(error => {
                enqueueSnackbar((error.response && error.response.data && error.response.data.message) || 'Saving failed', { variant: 'error' });
            });
    }

    function reconcileUpTo() {
        axios({
            method: 'PUT',
            url: '/reconciliation/reconcileupto',
            headers: { 'X-SocietyId': selectedSociety },
            data: { accountCode: accountCode, date: reconcileUpToDate }
        })
            .then(() => {
                setShowReconcileUpTo(false);
                enqueueSnackbar('Marked as cleared', { variant: 'success' });
                setRefreshData(refreshData + 1);
            })
            .catch(error => {
                enqueueSnackbar((error.response && error.response.data && error.response.data.message) || 'Saving failed', { variant: 'error' });
            });
    }

    function copyTransactionDate(row) {
        setBankDates({ ...bankDates, [row.id]: String(row.date).substring(0, 10) });
    }

    // "=" copies the transaction date and Enter moves to the bank date of the next row
    function handleBankDateKeyDown(e, row) {
        if (e.key === '=') {
            e.preventDefault();
            copyTransactionDate(row);
        } else if (e.key === 'Enter') {
            const inputs = Array.from(document.querySelectorAll('tbody input[inputmode="numeric"]'));
            const next = inputs[inputs.indexOf(e.target) + 1];
            if (next) next.focus();
        }
    }

    const isAdministrator = window.decodedJwt.role === 'Administrator';

    return (
        <>
            <Typography variant="h5">Bank Reconciliation</Typography>
            <div className={classes.topBar}>
                <Grid container>
                    <Grid item xs={4}>
                        <Autocomplete
                            options={accounts}
                            getOptionLabel={accountLabel}
                            selectOnFocus
                            disableOpenOnFocus
                            size="small"
                            renderInput={params => <TextField {...params} InputLabelProps={{ shrink: true }} label="Bank Account" autoFocus />}
                            onChange={(_, value) => { setAccountCode(value ? value.code : ''); setReconciliation({ items: [], bookBalance: 0 }); }}
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <PeriodSelector fromDate={fromDate} toDate={toDate} setFromDate={setFromDate} setToDate={setToDate} />
                        <TextField
                            select
                            size="small"
                            label="Show"
                            className={`${classes.topBarControl} ${classes.statusSelect}`}
                            value={status}
                            onChange={e => setStatus(e.target.value)}
                        >
                            <MenuItem value="uncleared">Uncleared</MenuItem>
                            <MenuItem value="cleared">Cleared</MenuItem>
                            <MenuItem value="all">All</MenuItem>
                        </TextField>
                        {isAdministrator && <>
                            <Button className={classes.topBarControl} onClick={save} variant="contained" size="small" color="primary"
                                disabled={!accountCode || changedRows.length === 0 || hasErrors}>Save</Button>
                            <Button className={classes.topBarControl} onClick={() => { setReconcileUpToDate(''); setShowReconcileUpTo(true); }}
                                variant="contained" size="small" disabled={!accountCode}>Mark Cleared Up To...</Button>
                            <Button className={classes.topBarControl} onClick={() => setShowImport(true)}
                                variant="contained" size="small" color="primary" disabled={!accountCode}>Import Statement</Button>
                        </>}
                    </Grid>
                </Grid>
            </div>
            {accountCode && <Paper className={classes.summary}>
                <div className={classes.summaryRow}>
                    <span>Balance as per books on {moment(toDate).format('DD/MM/YYYY')}</span>
                    <span>{formatMoney(reconciliation.bookBalance)}</span>
                </div>
                <div className={classes.summaryRow}>
                    <span>Less: Deposits not yet credited by bank</span>
                    <span>{formatMoney(notCredited)}</span>
                </div>
                <div className={classes.summaryRow}>
                    <span>Add: Cheques issued but not yet presented</span>
                    <span>{formatMoney(notPresented)}</span>
                </div>
                <div className={classes.summaryTotal}>
                    <span>Balance as per bank</span>
                    <span>{formatMoney(bankBalance)}</span>
                </div>
                <div className={classes.summaryRow}>
                    <TextField
                        type="number"
                        size="small"
                        label="Bank statement balance"
                        InputLabelProps={{ shrink: true }}
                        value={statementBalance}
                        onChange={e => setStatementBalance(e.target.value)}
                    />
                    {difference !== null && <span className={Math.abs(difference) >= 0.005 ? classes.difference : undefined}>
                        Difference: {formatMoney(difference)}
                    </span>}
                </div>
            </Paper>}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Particulars</TableCell>
                            <TableCell align="right">Deposit</TableCell>
                            <TableCell align="right">Withdrawal</TableCell>
                            <TableCell>Bank Date</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(row => (
                            <TableRow hover key={row.id}>
                                <TableCell>{moment(row.date).format('DD/MM/YYYY')}</TableCell>
                                <TableCell>
                                    <div><b>{itemName(row)}</b></div>
                                    <div>
                                        {typeNames[row.type] || row.type} {row.docNo}
                                        {row.chequeNo && <> &middot; Chq {row.chequeNo}</>}
                                        {row.chequeBank && <> &middot; {row.chequeBank}</>}
                                    </div>
                                    {row.narration && <div>{row.narration}</div>}
                                </TableCell>
                                <TableCell align="right">{row.deposit ? formatMoney(row.deposit) : ''}</TableCell>
                                <TableCell align="right">{row.withdrawal ? formatMoney(row.withdrawal) : ''}</TableCell>
                                <TableCell>
                                    <div className={classes.bankDateCell}>
                                        <DateField
                                            className={classes.bankDate}
                                            size="small"
                                            value={bankDate(row)}
                                            disabled={!isAdministrator}
                                            error={Boolean(bankDateError(row))}
                                            helperText={bankDateError(row)}
                                            onKeyDown={e => handleBankDateKeyDown(e, row)}
                                            onChange={value => setBankDates({ ...bankDates, [row.id]: value })}
                                        />
                                        <Tooltip title={bankDate(row) ? 'Clear bank date' : 'Same as transaction date (=)'}>
                                            <span>
                                                <Checkbox
                                                    className={classes.clearedCheckbox}
                                                    size="small"
                                                    color="primary"
                                                    tabIndex={-1}
                                                    checked={Boolean(bankDate(row))}
                                                    disabled={!isAdministrator}
                                                    inputProps={{ 'aria-label': 'Cleared by bank' }}
                                                    onChange={e => e.target.checked
                                                        ? copyTransactionDate(row)
                                                        : setBankDates({ ...bankDates, [row.id]: '' })}
                                                />
                                            </span>
                                        </Tooltip>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                        {accountCode && rows.length === 0 && <TableRow>
                            <TableCell colSpan={5} align="center">No transactions</TableCell>
                        </TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>
            <StatementImportDialog
                open={showImport}
                accountCode={accountCode}
                selectedSociety={selectedSociety}
                onClose={() => setShowImport(false)}
                onApplied={statement => {
                    setShowImport(false);
                    // Check the statement's closing balance against the books on its last date
                    setToDate(statement.toDate);
                    setStatementBalance(String(statement.closingBalance));
                    setRefreshData(refreshData + 1);
                }}
            />
            <Dialog open={showReconcileUpTo} onClose={() => setShowReconcileUpTo(false)}>
                <DialogTitle>Mark Cleared Up To</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Every uncleared transaction of this bank account dated on or before this date will be marked as cleared on its own date.
                        Use this once when starting reconciliation, with the date of the last bank statement that matched the books.
                    </DialogContentText>
                    <DateField label="Up to" value={reconcileUpToDate} onChange={setReconcileUpToDate} />
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="primary" onClick={reconcileUpTo} disabled={!reconcileUpToDate}>Mark Cleared</Button>
                    <Button variant="contained" color="secondary" onClick={() => setShowReconcileUpTo(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default connect(mapToProps, actions)(BankReconciliation);
